import React, { useEffect, useState, useMemo } from 'react';
import parse from 'html-react-parser';
import { create } from 'apisauce';
import clsx from 'clsx';

import { getPublicNote, getPublicTemplate } from 'api/notes';
import { PageBranding } from 'components/Branding';
import APModal from 'components/Modal/APModal';
import { Spinner } from 'components/Loading';
import { getGoogleFonts, GOOGLE_FONT_LINK_ID } from 'components/PlateEditor/plugins/fonts/createFontFamilyPlugin';
import { decipherID } from 'utils/idCipher';
import { isFileImageExtension } from 'utils/general';
import { transformHtmlToLightbox } from '../../../components/Lightbox/utils/transformHtmlToLightbox';

import './note-preview.scss';

const NotePreview = ({ encryptedID, isTemplate }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [html, setHtml] = useState();
  const [fileUrl, setFileUrl] = useState();
  const [filename, setFilename] = useState();
  const [isImage, setIsImage] = useState();
  const [branding, setBranding] = useState(null);
  const [orgId, setOrgId] = useState();
  const [projectId, setProjectId] = useState();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showBrandingHeader, setShowBrandingHeader] = useState(false);

  const [downloadFailed, setDownloadFailed] = useState(false);

  // Set margins for page.
  const {
    margin_bottom_pt,
    margin_top_pt,
    margin_left_pt,
    margin_right_pt,
  } = branding?.template_dict || {};

  const pageStyle = {
    paddingTop: `${margin_top_pt}pt`,
    paddingRight: `${margin_right_pt}pt`,
    paddingBottom: `${margin_bottom_pt}pt`,
    paddingLeft: `${margin_left_pt}pt`,
    ... branding?.template_dict && {
      '--pagePaddingTop': `${margin_top_pt}pt`,
      '--pagePaddingRight': `${margin_right_pt + 1}pt`,
      '--pagePaddingBottom': `${margin_bottom_pt}pt`,
      '--pagePaddingLeft': `${margin_left_pt + 1}pt`,
    },
  };

  const noteId = useMemo(() => {
    return decipherID(encryptedID);
  }, [encryptedID]);

  useEffect(() => {
    const getPublicItem = isTemplate ? getPublicTemplate : getPublicNote;
    const getNoteContent = async (id) => {
      try {
        const response = await getPublicItem({ id });
        const {
          is_file_upload,
          show_branding_header,
          file_url,
          filename: _filename,
          note_html,
          branding: _branding,
          org_id,
          project_id,
        } = response.data || {};
        setShowBrandingHeader(show_branding_header ?? false);

        // To download notes/templates, you need project id for notes,
        // org id for templates.
        if (org_id) setOrgId(org_id);
        if (project_id) setProjectId(project_id);

        if (note_html != null) {
          setHtml(note_html);
        }

        if (is_file_upload && file_url) {
          setFileUrl(file_url);
          setFilename(_filename);
          setIsImage(isFileImageExtension(_filename));
        }

        if (_branding) {
          setBranding(_branding);
        }
        setIsLoading(false);
      } catch(e) {
        setError(e);
      }
    };
    if (noteId) {
      getNoteContent(noteId);
    }

    // Load fonts
    if (!document.getElementById(GOOGLE_FONT_LINK_ID)) {
      getGoogleFonts();
    }
  }, []);

  const handleDownload = async () => {
    if (fileUrl) {
      if (isImage) {
        const extension = filename.split('.').pop();
        const mimeType = `image/${extension}`;
        downloadImage(fileUrl, filename, mimeType);
      } else {
        downloadPdf(fileUrl, filename);
      }
    }
    else {
      startPdfDownload();
    }
  };

  const startPdfDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await streamDownload({
        projectId,
        orgId,
        params: { note_id: noteId },
      });
      setIsDownloading(false);
      if (response.status !== 401) window.location.replace(response.data.url);
    }
    catch {
      setIsDownloading(false);
      setDownloadFailed(true);
    }
  };

  if (isLoading) {
    return (
      <div className="project-note-preview">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-note-preview">
        <p>Something went wrong. Please try again or contact support.</p>
      </div>
    );
  }

  const FileViewer = () => {
    if (isImage) {
      return (
        <div className="project-note-preview__image">
          <img src={fileUrl} />
        </div>
      );
    }
    return (
      <div className="project-note-preview__iframe">
        <iframe
          src={
            `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`
          }
          title="Note file viewer"
        />
      </div>
    );
  };
  const Content = () => {
    if (fileUrl) return <FileViewer />;
    return (
      <div
        className={clsx(
          'project-note-preview__document ap-slate-editor',
          showBrandingHeader && 'project-note--has-header',
        )}
        style={pageStyle}
      >
        {showBrandingHeader && (
          <PageBranding
            logo={branding.logo}
            template={branding.template_dict}
          />
        )}
        {parse(html ?? '', {
          transform: transformHtmlToLightbox,
        })}
      </div>
    );
  };

  return (
    <>
      <div className="project-note-preview">
        <Content />
        <div className="project-note-preview__footer">
          <div className="project-note-preview__actions">
            {!isTemplate && (
              <>
                <button
                  className={clsx('button--unstyled', 'download-link', 'left', isDownloading && 'loading')}
                  onClick={handleDownload}
                >
                  Download
                </button>
                {/* Don't need print at this time.
                |
                <button
                  className="button--unstyled"
                  onClick={() => {
                    window.print();
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'print']} />
                  Print
                </button>
                */}
              </>
            )}

          </div>
        </div>
      </div>
      {downloadFailed && (
        <APModal
          open
          title="Oops!"
          name="download-failed"
          onClose={() => setDownloadFailed(false)}
        >
          We were not able to prepare your download. Please try again or contact support.
        </APModal>
      )}
    </>
  );
};

export const downloadImage = (fileUrl, filename) => {
  fetch(fileUrl, {
    method: 'GET',
    cache: 'reload',
  })
  .then((response) => response.blob())
  .then((blob) => {
    // Create blob link to download
    const url = window.URL.createObjectURL(
      new Blob([blob]),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      filename,
    );

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  });
};

export const downloadPdf = (fileUrl, filename) => {
  fetch(fileUrl, {
    method: 'GET',
  })
  .then((response) => response.blob())
  .then((blob) => {
    // Create blob link to download
    const url = window.URL.createObjectURL(
      new Blob([blob]),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      filename,
    );

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  });
};

// eslint-disable-next-line
const streamDownload = ({ projectId, orgId, params }) => {
  return new Promise((resolve, reject) => {
    const input = {
      client_offset: (new Date().getTimezoneOffset()),
      download_type: projectId ? 'wedding.note' :
        orgId ? 'organization.note_template' : null,
      params,
    };
    const api = create({
      baseURL: `${process.env.REACT_APP_AP_URL}`,
      headers: {
        'content-type': 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    const url = projectId ? `/api/wedding/${projectId}/downloads`
      : `/api/organization/${orgId}/downloads`;

    try {
      api
        .post(url, input)
        .then((response) => {
          if (response.status === 401) {
            resolve(response);
          }
          poll(response, api, input, projectId, orgId)
            .then((downloadResponse) => {
              resolve(downloadResponse);
            })
            .catch((error) => {
              return reject(error);
            });
        });
    } catch (error) {
      return Promise.reject(error);
    }
  });
};

const poll = (response, api, input, projectId, orgId) => {
  const url = projectId ? `/api/wedding/${projectId}/note/download/${response.data.token}`
    : `/api/organization/${orgId}/wedding_note_templates/download/${response.data.token}`;
  return new Promise((resolve, reject) => {
    if (response.data.status === 'ready') {
      resolve(response);
    } else if (response.data.status === 'error') {
      reject(new Error('Promise failed'));
    } else {
      setTimeout(() => {
        try {
          api
            .get(
              url,
              input,
            )
            .then((getResponse) => {
              resolve(
                poll(getResponse, api, input, projectId, orgId).catch((error) => {
                  return Promise.reject(error);
                }),
              );
            });
        } catch (error) {
          reject(error);
        }
      }, 3000);
    }
  });
};

export default NotePreview;
